// Block-Climb decor (Level 9): "Race to the Top" — a colourful uphill
// course built out of chunky stair-block decoration around a smooth
// up-ramp path. Pink rails on both sides, neon arrow decals on the
// safe pads, ball-cannon meshes at the top of each climb, big TOP!
// finish arch at the end. Visual layout only; the physics walks the
// smooth ramp profile defined by Path.heightAt.

import * as THREE from "three";
import { SoftBox } from "./roundedBoxes";
import type { Path } from "./Path";

const TRACK_TOP_Y_BASE = 0.18;
const RAIL_COLOR = 0xff5fa7;
const RAIL_TRIM = 0xffd166;
const BASE_BLUE = 0x4cc9ff;
const SAFE_GREEN = 0x6be09b;
const GATE_PURPLE = 0x9b5cf6;
const CANNON_BLUE = 0x4f5dff;
const CANNON_PINK = 0xff5fa7;
const CANNON_YELLOW = 0xffd166;

// Block palette per the brief — yellow / blue / pink / green / orange,
// plus a couple of accents.
const BLOCK_COLORS = [
  0xffd166, 0x6bd0ff, 0xff5fa7, 0x6be09b, 0xff8a3d,
  0xa855f7, 0xfde047, 0x4cc9ff,
];

export interface BlockClimbHandle {
  group: THREE.Group;
  /** Always-supported meshes — every visible deck/pad/finish goes here
   *  so the standable raycaster catches the player on the smooth ramp. */
  supportMeshes: THREE.Object3D[];
  /** Gate s-positions exposed to the Spawner. */
  gatePositions: number[];
  tick: (clockMs: number, dt: number) => void;
  /** Returns the first ball overlapping the player, plus a knock direction
   *  (along -s, since balls roll down-slope), so Game.ts can apply a
   *  knockdown and clear the ball. Returns null when nothing is hit. */
  ballContact: (s: number, l: number, radius: number) => {
    normalS: number;
    normalL: number;
    penetration: number;
    /** Identity so the engine can spend the ball after one knock. */
    consume: () => void;
  } | null;
}

/** Single chunky climb-block (decorative). */
function makeBlock(width: number, height: number, depth: number, color: number, cracked = false): THREE.Mesh {
  const mat = new THREE.MeshStandardMaterial({
    color,
    roughness: cracked ? 0.7 : 0.55,
    emissive: cracked ? 0x222222 : 0x000000,
    emissiveIntensity: cracked ? 0.15 : 0,
  });
  const m = new THREE.Mesh(new SoftBox(width, height, depth), mat);
  m.castShadow = true;
  m.receiveShadow = true;
  return m;
}

/** Tall pink side rail running along a section of s. */
function makeRail(length: number, sideSign: 1 | -1, halfWidth: number, baseY: number, topY: number): THREE.Group {
  const g = new THREE.Group();
  const mat = new THREE.MeshStandardMaterial({ color: RAIL_COLOR, roughness: 0.45 });
  const trimMat = new THREE.MeshStandardMaterial({ color: RAIL_TRIM, roughness: 0.4, metalness: 0.25 });
  const rise = topY - baseY;
  const midY = baseY + rise / 2;
  const slabHeight = Math.max(0.4, rise + 1.4);
  const slab = new THREE.Mesh(new SoftBox(0.55, slabHeight, length), mat);
  slab.position.set(sideSign * (halfWidth + 0.2), midY + 0.7, 0);
  // Slight rotation around X to follow the slope visually.
  slab.rotation.x = Math.atan2(rise, length);
  g.add(slab);
  // Gold top cap.
  const cap = new THREE.Mesh(new SoftBox(0.55, 0.18, length), trimMat);
  cap.position.set(sideSign * (halfWidth + 0.2), midY + 0.7 + slabHeight / 2 + 0.1, 0);
  cap.rotation.x = slab.rotation.x;
  g.add(cap);
  return g;
}

/** Cannon prop at the top of each climb — colourful tube facing down
 *  the slope. Three pieces: barrel, base, glow tip. */
function makeCannon(color: number): THREE.Group {
  const g = new THREE.Group();
  const base = new THREE.Mesh(
    new THREE.CylinderGeometry(0.55, 0.7, 0.8, 18),
    new THREE.MeshStandardMaterial({ color: 0x6c43c5, roughness: 0.45 }),
  );
  base.position.y = 0.4;
  g.add(base);
  const barrel = new THREE.Mesh(
    new THREE.CylinderGeometry(0.45, 0.55, 1.6, 22),
    new THREE.MeshStandardMaterial({ color, emissive: color, emissiveIntensity: 0.18, roughness: 0.45 }),
  );
  barrel.rotation.z = Math.PI / 2;
  barrel.position.set(0, 1.0, 0);
  barrel.rotation.x = -0.4; // angle the muzzle slightly downward (down-slope)
  g.add(barrel);
  const tip = new THREE.Mesh(
    new THREE.TorusGeometry(0.5, 0.1, 10, 24),
    new THREE.MeshStandardMaterial({ color: CANNON_YELLOW, emissive: CANNON_YELLOW, emissiveIntensity: 0.45 }),
  );
  tip.rotation.x = Math.PI / 2;
  tip.position.set(0, 1.0, -0.95);
  g.add(tip);
  return g;
}

export function buildBlockClimbCourse(path: Path): BlockClimbHandle {
  const bc = path.blockClimb;
  if (!bc) throw new Error("buildBlockClimbCourse called on non-block-climb path");

  const group = new THREE.Group();
  const supportMeshes: THREE.Object3D[] = [];
  const trackWidth = bc.halfWidth * 2;

  // ── 1. Start area: large flat blue base + "RACE TO THE TOP!" sign ──
  const startEnd = bc.climbs[0]!.start;
  const startBase = new THREE.Mesh(
    new SoftBox(trackWidth + 1.6, 0.5, startEnd),
    new THREE.MeshStandardMaterial({ color: BASE_BLUE, roughness: 0.55 }),
  );
  // Mount the start slab on the path so it follows its tangent in case
  // the start span happens to straddle one of the new corners.
  {
    const mid = startEnd / 2;
    const p = path.positionAt(mid, 0);
    startBase.position.set(p.x, TRACK_TOP_Y_BASE - 0.25, p.z);
    startBase.rotation.y = path.yawAt(mid);
  }
  startBase.receiveShadow = true;
  startBase.castShadow = true;
  group.add(startBase);
  supportMeshes.push(startBase);
  // RACE sign post.
  const signGroup = new THREE.Group();
  for (const x of [-2.4, 2.4]) {
    const post = new THREE.Mesh(
      new THREE.CylinderGeometry(0.16, 0.18, 3.4, 12),
      new THREE.MeshStandardMaterial({ color: 0x6c43c5, roughness: 0.45 }),
    );
    post.position.set(x, 1.7 + TRACK_TOP_Y_BASE, 0);
    signGroup.add(post);
  }
  const board = new THREE.Mesh(
    new SoftBox(5.8, 1.0, 0.3),
    new THREE.MeshStandardMaterial({ color: RAIL_COLOR, emissive: 0xff5fa7, emissiveIntensity: 0.18 }),
  );
  board.position.set(0, 3.1 + TRACK_TOP_Y_BASE, 0);
  signGroup.add(board);
  {
    const sSign = startEnd - 2.4;
    const p = path.positionAt(sSign, 0);
    signGroup.position.set(p.x, 0, p.z);
    signGroup.rotation.y = path.yawAt(sSign);
  }
  group.add(signGroup);

  // ── 2. Climb decoration: stacked cubes along each climb section ──
  // We DON'T add these to supportMeshes — the heightAt smooth ramp
  // already handles physics. The blocks are purely visual stair art.
  for (let ci = 0; ci < bc.climbs.length; ci++) {
    const climb = bc.climbs[ci]!;
    const climbLen = climb.end - climb.start;
    const rise = climb.endY - climb.startY;
    // Render a staircase that mirrors the climb. Each visual step is
    // ~1.2 m deep / ~0.55 m tall. The actual physics height curves
    // smoothly between them so the player walks up cleanly.
    const stepDepth = 1.2;
    const steps = Math.max(4, Math.round(climbLen / stepDepth));
    const stepRise = rise / steps;
    // 4 lanes of stair blocks visible across the width.
    const lanes = 5;
    const laneWidth = (trackWidth - 0.6) / lanes;
    for (let i = 0; i < steps; i++) {
      const sStep = climb.start + (i + 0.5) * (climbLen / steps);
      // The TOP of step i should sit at climb.startY + (i+1) * stepRise.
      const topY = climb.startY + (i + 1) * stepRise;
      // Block thickness varies per lane for visual texture; height is
      // a chunk taller than the smooth ramp so the cube reads as a
      // climbable platform without breaking the physics surface.
      for (let l = 0; l < lanes; l++) {
        const lx = -trackWidth / 2 + 0.3 + (l + 0.5) * laneWidth;
        const blockColor = BLOCK_COLORS[(i * 3 + l * 7) % BLOCK_COLORS.length]!;
        const blockHeight = 0.85 + Math.random() * 0.15;
        const cracked = (i + l) % 7 === 0; // sprinkle of cracked-look blocks
        const block = makeBlock(laneWidth * 0.95, blockHeight, stepDepth * 0.95, blockColor, cracked);
        const wp = path.positionAt(sStep, lx);
        block.position.set(wp.x, topY - blockHeight / 2, wp.z);
        block.rotation.y = path.yawAt(sStep);
        group.add(block);
      }
    }
    // Pink side rails along the climb.
    const railL = makeRail(climbLen, -1, bc.halfWidth, climb.startY, climb.endY);
    {
      const mid = climb.start + climbLen / 2;
      const p = path.positionAt(mid, 0);
      railL.position.set(p.x, 0, p.z);
      railL.rotation.y = path.yawAt(mid);
    }
    group.add(railL);
    const railR = makeRail(climbLen, 1, bc.halfWidth, climb.startY, climb.endY);
    {
      const mid = climb.start + climbLen / 2;
      const p = path.positionAt(mid, 0);
      railR.position.set(p.x, 0, p.z);
      railR.rotation.y = path.yawAt(mid);
    }
    group.add(railR);
  }

  // ── 3. Safe pads (green base + neon arrows + checkpoint gate arch) ──
  for (let pi = 0; pi < bc.pads.length; pi++) {
    const pad = bc.pads[pi]!;
    const len = pad.end - pad.start;
    const isFinishPad = pi === bc.pads.length - 1;
    const padMesh = new THREE.Mesh(
      new SoftBox(trackWidth + 1.6, 0.5, len),
      new THREE.MeshStandardMaterial({
        color: isFinishPad ? 0xfff3a8 : SAFE_GREEN,
        roughness: 0.55,
      }),
    );
    {
      const mid = pad.start + len / 2;
      const p = path.positionAt(mid, 0);
      padMesh.position.set(p.x, pad.y - 0.25, p.z);
      padMesh.rotation.y = path.yawAt(mid);
    }
    padMesh.receiveShadow = true;
    padMesh.castShadow = true;
    group.add(padMesh);
    supportMeshes.push(padMesh);

    // White arrow chevrons on the pad pointing forward.
    for (let i = 0; i < 4; i++) {
      const arrow = new THREE.Mesh(
        new THREE.PlaneGeometry(2.6, 0.8),
        new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.78 }),
      );
      arrow.rotation.x = -Math.PI / 2;
      const sArrow = pad.start + (i + 1) * (len / 5);
      const pa = path.positionAt(sArrow, 0);
      arrow.position.set(pa.x, pad.y + 0.01, pa.z);
      arrow.rotation.z = path.yawAt(sArrow);
      group.add(arrow);
    }

    // Side rails along the pad (flat).
    const padRailL = makeRail(len, -1, bc.halfWidth, pad.y, pad.y);
    {
      const mid = pad.start + len / 2;
      const p = path.positionAt(mid, 0);
      padRailL.position.set(p.x, 0, p.z);
      padRailL.rotation.y = path.yawAt(mid);
    }
    group.add(padRailL);
    const padRailR = makeRail(len, 1, bc.halfWidth, pad.y, pad.y);
    {
      const mid = pad.start + len / 2;
      const p = path.positionAt(mid, 0);
      padRailR.position.set(p.x, 0, p.z);
      padRailR.rotation.y = path.yawAt(mid);
    }
    group.add(padRailR);

    // Gate arch (purple). Placeholder — no question logic, just visual.
    if (!isFinishPad) {
      const gateY = pad.y;
      const sGate = pad.start + len * 0.6;
      const yawGate = path.yawAt(sGate);
      const gateCenter = path.positionAt(sGate, 0);
      // Posts placed perpendicular to the path tangent.
      const sideX = Math.cos(yawGate);
      const sideZ = -Math.sin(yawGate);
      for (const side of [-1, 1] as const) {
        const dist = (bc.halfWidth + 0.3) * side;
        const post = new THREE.Mesh(
          new THREE.CylinderGeometry(0.22, 0.26, 3.4, 12),
          new THREE.MeshStandardMaterial({ color: GATE_PURPLE, emissive: GATE_PURPLE, emissiveIntensity: 0.18 }),
        );
        post.position.set(gateCenter.x + sideX * dist, gateY + 1.7, gateCenter.z + sideZ * dist);
        group.add(post);
      }
      const arch = new THREE.Mesh(
        new SoftBox(trackWidth + 1.2, 0.45, 0.45),
        new THREE.MeshStandardMaterial({ color: GATE_PURPLE, emissive: 0x6428e0, emissiveIntensity: 0.3 }),
      );
      arch.position.set(gateCenter.x, gateY + 3.55, gateCenter.z);
      arch.rotation.y = yawGate;
      group.add(arch);
    }
  }

  // ── 4. Ball cannons at the top of each climb (animated firing) ──
  interface CannonSlot {
    s: number;
    l: number;
    color: number;
    nextFireMs: number;
  }
  const cannonSlots: CannonSlot[] = [];
  for (const cn of bc.cannons) {
    // Pick a colour based on lateral position so the lineup pops.
    const color = cn.l < -1 ? CANNON_PINK : cn.l > 1 ? CANNON_BLUE : CANNON_YELLOW;
    const cannon = makeCannon(color);
    const y = path.heightAt(cn.s) + 0.4;
    const cs = cn.s + 0.7;
    const wp = path.positionAt(cs, cn.l);
    cannon.position.set(wp.x, y, wp.z);
    // Face the down-slope direction (back along the path tangent).
    cannon.rotation.y = path.yawAt(cs) + Math.PI;
    group.add(cannon);
    // Stagger first-fire so all cannons don't shoot in lockstep.
    cannonSlots.push({
      s: cn.s,
      l: cn.l,
      color,
      nextFireMs: 800 + Math.floor(Math.random() * 1600),
    });
  }
  /** A single live ball flying down the ramp. */
  interface FlyingBall {
    mesh: THREE.Mesh;
    s: number; // forward arc-length position (decreases over time)
    l: number; // lateral offset
    vs: number; // -Z velocity (m/s) — negative because we roll down-slope
    vl: number; // lateral velocity (small drift)
    radius: number;
  }
  const ballMat = (color: number) => new THREE.MeshStandardMaterial({
    color, emissive: color, emissiveIntensity: 0.25, roughness: 0.35, metalness: 0.1,
  });
  const ballGeo = new THREE.SphereGeometry(0.4, 14, 12);
  const balls: FlyingBall[] = [];
  const FIRE_INTERVAL_MIN = 2200;
  const FIRE_INTERVAL_MAX = 3800;
  const BALL_SPEED = 7.5;       // m/s down-slope
  const BALL_RADIUS = 0.4;
  const startSEnd = bc.climbs[0]!.start;

  // ── 5. Finish arch: big TOP! sign with a crown crown banner ──
  const finishY = bc.pads[bc.pads.length - 1]!.y;
  const finishS = bc.length - 6;
  const finishArchMat = new THREE.MeshStandardMaterial({
    color: 0xffd166,
    emissive: 0xff8a3d,
    emissiveIntensity: 0.35,
    roughness: 0.4,
  });
  {
    const yawF = path.yawAt(finishS);
    const fc = path.positionAt(finishS, 0);
    const sideX = Math.cos(yawF);
    const sideZ = -Math.sin(yawF);
    for (const side of [-1, 1] as const) {
      const post = new THREE.Mesh(
        new THREE.CylinderGeometry(0.36, 0.42, 5.0, 16),
        finishArchMat,
      );
      const d = (bc.halfWidth + 0.4) * side;
      post.position.set(fc.x + sideX * d, finishY + 2.5, fc.z + sideZ * d);
      post.castShadow = true;
      group.add(post);
    }
    const finishBeam = new THREE.Mesh(
      new SoftBox(trackWidth + 1.6, 1.3, 0.6),
      finishArchMat,
    );
    finishBeam.position.set(fc.x, finishY + 5.2, fc.z);
    finishBeam.rotation.y = yawF;
    group.add(finishBeam);
    const topBadge = new THREE.Mesh(
      new SoftBox(2.6, 1.0, 0.4),
      new THREE.MeshStandardMaterial({ color: 0xff5fa7, emissive: 0xff5fa7, emissiveIntensity: 0.5 }),
    );
    // Tangent direction along the path so the badge sits behind the beam.
    const tanX = Math.sin(yawF);
    const tanZ = Math.cos(yawF);
    topBadge.position.set(fc.x - tanX * 0.1, finishY + 6.0, fc.z - tanZ * 0.1);
    topBadge.rotation.y = yawF;
    group.add(topBadge);
    // Confetti balloons behind the finish arch — laid out along the path
    // perpendicular so they stay tidy after a turn.
    const balloonColors = [0xff5fa7, 0xffd166, 0x6bd0ff, 0x6be09b, 0xa855f7];
    for (let i = 0; i < 24; i++) {
      const c = balloonColors[i % balloonColors.length]!;
      const b = new THREE.Mesh(
        new THREE.SphereGeometry(0.46, 12, 10),
        new THREE.MeshStandardMaterial({ color: c, emissive: c, emissiveIntensity: 0.2 }),
      );
      // Slot the balloons in a strip 2-5m past the finish arch.
      const lateral = -trackWidth / 2 - 2 + (i / 24) * (trackWidth + 4);
      const sAhead = finishS + 2 + (i % 2) * 1.4;
      const bp = path.positionAt(sAhead, lateral);
      b.position.set(bp.x, finishY + 6.5 + Math.sin(i * 1.7) * 0.8, bp.z);
      group.add(b);
    }
  }

  // ── 6. Light sky-blue cloud puffs on either side for atmosphere ──
  for (let i = 0; i < 18; i++) {
    const cloud = new THREE.Mesh(
      new THREE.SphereGeometry(1.6 + Math.random() * 1.4, 10, 8),
      new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.88 }),
    );
    const side = i % 2 === 0 ? -1 : 1;
    const sCloud = (i / 18) * bc.length;
    const cl = side * (bc.halfWidth + 8 + Math.random() * 8);
    const cp = path.positionAt(sCloud, cl);
    cloud.position.set(cp.x, 4 + Math.random() * 8, cp.z);
    group.add(cloud);
  }

  let lastClockMs = 0;
  const tick = (clockMs: number, dt: number) => {
    const dtSec = dt > 0 ? dt : Math.max(0, (clockMs - lastClockMs) / 1000);
    lastClockMs = clockMs;
    // Fire balls on cooldown.
    for (const slot of cannonSlots) {
      if (clockMs < slot.nextFireMs) continue;
      slot.nextFireMs = clockMs + FIRE_INTERVAL_MIN + Math.random() * (FIRE_INTERVAL_MAX - FIRE_INTERVAL_MIN);
      const m = new THREE.Mesh(ballGeo, ballMat(slot.color));
      m.castShadow = true;
      const startY = path.heightAt(slot.s) + 0.6;
      {
        const wp = path.positionAt(slot.s + 0.2, slot.l);
        m.position.set(wp.x, startY, wp.z);
      }
      group.add(m);
      balls.push({
        mesh: m,
        s: slot.s,
        l: slot.l,
        vs: -BALL_SPEED,
        vl: (Math.random() - 0.5) * 0.8,
        radius: BALL_RADIUS,
      });
    }
    // Advance live balls.
    for (let i = balls.length - 1; i >= 0; i--) {
      const b = balls[i]!;
      b.s += b.vs * dtSec;
      b.l += b.vl * dtSec;
      const y = path.heightAt(b.s) + b.radius + 0.18;
      const wp = path.positionAt(b.s, b.l);
      b.mesh.position.set(wp.x, y, wp.z);
      b.mesh.rotation.x -= dtSec * 6;
      // Despawn when the ball rolls past the start area (off the bottom of
      // the course) or drifts too far laterally.
      if (b.s <= startSEnd - 2 || Math.abs(b.l) > bc.halfWidth + 4) {
        group.remove(b.mesh);
        (b.mesh.material as THREE.Material).dispose?.();
        balls.splice(i, 1);
      }
    }
    // (Falling balls were stubbed in the original — now wired above.)
    // added later, layered on top of the existing obstacle system.)
  };

  const ballContact = (s: number, l: number, radius: number) => {
    for (const b of balls) {
      const ds = b.s - s;
      const dl = b.l - l;
      const sumR = b.radius + radius;
      const dist2 = ds * ds + dl * dl;
      if (dist2 >= sumR * sumR) continue;
      const dist = Math.sqrt(Math.max(0.0001, dist2));
      // Normal points FROM the ball TO the player (i.e. push direction).
      const nl = dist > 0.001 ? -dl / dist : 0;
      const ns = dist > 0.001 ? -ds / dist : -1;
      return {
        normalS: ns,
        normalL: nl,
        penetration: sumR - dist,
        consume: () => {
          group.remove(b.mesh);
          (b.mesh.material as THREE.Material).dispose?.();
          const idx = balls.indexOf(b);
          if (idx >= 0) balls.splice(idx, 1);
        },
      };
    }
    return null;
  };

  return {
    group,
    supportMeshes,
    gatePositions: bc.gates,
    tick,
    ballContact,
  };
}
